import type { SidebarIconName } from './SidebarIcon';

/**
 * Stable identifiers for the Workspace-mode navigation entries. These ids are persisted in
 * personal sidebar layouts, so renaming one is a breaking change for saved layouts.
 */
export type WorkspaceNavItemId =
  | 'dashboard'
  | 'contacts'
  | 'assets'
  | 'products'
  | 'services'
  | 'invoices'
  | 'payments'
  | 'calendar'
  | 'rostering'
  | 'fieldOperations'
  | 'consultations'
  | 'tasks'
  | 'reminderEngine';

export type WorkspaceNavItem = {
  id: WorkspaceNavItemId;
  to: string;
  icon: SidebarIconName;
  /** Key inside the `nav` i18n namespace. */
  labelKey: string;
  /** Match the route exactly (NavLink `end`). */
  end?: boolean;
  /** Route prefix that counts as "inside" this entry when it differs from `to`. */
  activePrefix?: string;
};

/** Which workspace entries the current person may see, after permissions and plan entitlements. */
export type WorkspaceNavAccess = Record<WorkspaceNavItemId, boolean>;

/** Default order of the Workspace navigation. Personal layouts reorder or hide these. */
export const WORKSPACE_NAV_ITEMS: readonly WorkspaceNavItem[] = [
  { id: 'dashboard', to: '/dashboard', icon: 'dashboard', labelKey: 'dashboard' },
  { id: 'contacts', to: '/contacts', icon: 'contacts', labelKey: 'contacts', end: true },
  { id: 'assets', to: '/assets', icon: 'assets', labelKey: 'assets' },
  { id: 'products', to: '/products', icon: 'products', labelKey: 'products' },
  { id: 'services', to: '/services', icon: 'services', labelKey: 'services' },
  { id: 'invoices', to: '/invoices', icon: 'invoices', labelKey: 'invoices' },
  { id: 'payments', to: '/payments', icon: 'payments', labelKey: 'payments' },
  { id: 'calendar', to: '/calendar', icon: 'calendar', labelKey: 'calendar' },
  { id: 'rostering', to: '/rostering', icon: 'rostering', labelKey: 'rostering' },
  { id: 'fieldOperations', to: '/field-operations', icon: 'fieldOperations', labelKey: 'fieldOperations' },
  { id: 'consultations', to: '/consultations', icon: 'consultations', labelKey: 'consultations' },
  { id: 'tasks', to: '/tasks', icon: 'tasks', labelKey: 'tasks' },
  { id: 'reminderEngine', to: '/reminder-engine/definitions', icon: 'tasks', labelKey: 'reminderEngine', activePrefix: '/reminder-engine' },
];

export const accessibleWorkspaceNavItems = (access: WorkspaceNavAccess): WorkspaceNavItem[] =>
  WORKSPACE_NAV_ITEMS.filter(item => access[item.id]);
